import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spinner, Alert, Btn, Input, Select, Modal, ConfirmDialog, SectionLabel } from '../../components/UI';
import { api } from '../../utils/api';
import MarketSignals from './MarketSignals';

// Find clients — HB Sales Tool W3: the fit heat map with the criteria-and-period bar.
// Fits come from the server (services/basecampFit.js); filters, sorting and export run here.
// A cell opens a project at stage 0 for that client and programme; an outlined cell already has one.

const PERIODS = [[0, 'today'], [3, 'next 3 months'], [6, 'next 6 months'], [12, 'next 12 months']];
const WEIGHT_LEVELS = [['off', 0], ['low', 0.5], ['normal', 1], ['high', 1.5], ['very high', 2]];
const PRIORITY_LEVELS = [['normal', 0], ['priority (+10)', 10], ['high priority (+20)', 20]];
const PAGE = 50;
const FIT_METHOD = 'Fit = programme base + the points for each criterion the firm has × the criterion weight + size points × weight; limited by the programme cap and rounded to 5. Then the programme priority (★) and the market signals valid in the chosen period (◆) are added, for programmes the firm already fits. Maximum 100.';

// Same colour ramp as the visual contract: blue (low) → cream → red (high).
export function ramp(v) {
  const a = [142, 170, 219], m = [255, 242, 204], b = [232, 130, 124];
  const t = Math.max(0, Math.min(1, v));
  const [x, y, f] = t < 0.5 ? [a, m, t / 0.5] : [m, b, (t - 0.5) / 0.5];
  return `rgb(${x.map((c, i) => Math.round(c + (y[i] - c) * f)).join(',')})`;
}
export const empOf = (c) => (c.nbs ? 500 : (c.employees || 0));
export const cityLabel = (c) => ({ Beograd: 'Belgrade' }[c] || c);
export const empLabel = (c) => (c.nbs && !c.employees ? 'bank / insurer' : `${(c.employees || 0).toLocaleString('en-GB')}`);

const F0 = { q: '', city: [], list: ['A', 'B'], sector: '', municipality: '', empMin: '', empMax: '', revMin: '', own: 'any', noPublic: true, crit: [], progs: null, min: 50, onlyWarm: true, sort: 'potential' };

export default function FindClients() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(0);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  // "Find clients for this product" / "Customise for a client →" on a catalogue page arrive with ?programme=<slug>.
  const [f, setF] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('programme');
    return p ? { ...F0, progs: [p] } : F0;
  });
  const [showFilters, setShowFilters] = useState(false);
  const [adjust, setAdjust] = useState(false);
  const [cell, setCell] = useState(null);       // { client, programme } waiting for confirmation
  const [busy, setBusy] = useState(false);
  const [openError, setOpenError] = useState(null);
  const [page, setPage] = useState(0);

  const load = useCallback(() => {
    setState(s => ({ ...s, loading: true }));
    api.explore.findClients(period)
      .then(d => setState({ loading: false, error: null, data: d }))
      .catch(e => setState({ loading: false, error: e.message, data: null }));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const d = state.data;
  const scored = useMemo(() => (d ? d.programmes.filter(p => p.scored) : []), [d]);
  const progs = useMemo(() => scored.filter(p => !f.progs || f.progs.includes(p.slug)), [scored, f.progs]);
  const options = useMemo(() => {
    if (!d) return { cities: [], sectors: [], municipalities: [] };
    const count = (k) => d.clients.reduce((m, c) => { if (c[k]) m[c[k]] = (m[c[k]] || 0) + 1; return m; }, {});
    return { cities: Object.entries(count('city')).sort((a, b) => b[1] - a[1]), sectors: Object.keys(count('sector')).sort(), municipalities: Object.keys(count('municipality')).sort() };
  }, [d]);

  const rows = useMemo(() => {
    if (!d) return [];
    const q = f.q.trim().toLowerCase();
    let list = d.clients.filter(c => {
      if (q && !(`${c.name} ${c.short || ''} ${c.mb}`.toLowerCase().includes(q))) return false;
      if (f.city.length && !f.city.includes(c.city)) return false;
      if (f.list.length && !f.list.includes(c.list)) return false;
      if (f.sector && c.sector !== f.sector) return false;
      if (f.municipality && c.municipality !== f.municipality) return false;
      if (f.empMin !== '' && (c.employees || 0) < Number(f.empMin) && !c.nbs) return false;
      if (f.empMax !== '' && (c.employees || 0) > Number(f.empMax)) return false;
      if (f.revMin !== '' && (c.revenue || 0) / 1000 < Number(f.revMin)) return false;
      const foreign = c.criteria.includes('foreign owner (likely)');
      if (f.own === 'foreign' && !foreign) return false;
      if (f.own === 'domestic' && foreign) return false;
      if (f.noPublic && c.criteria.includes('public enterprise')) return false;
      if (f.crit.length && !f.crit.some(k => c.criteria.includes(k))) return false;
      return true;
    }).map(c => {
      const v = progs.map(p => c.fit[p.slug] || 0);
      return { c, max: Math.max(0, ...v), warm: v.filter(x => x >= f.min && x > 0).length };
    });
    if (f.onlyWarm) list = list.filter(r => r.warm > 0);
    const key = { potential: r => [r.c.potential, r.max], max: r => [r.max, r.warm], warm: r => [r.warm, r.max], revenue: r => [r.c.revenue || 0, 0], employees: r => [empOf(r.c), 0] }[f.sort];
    return list.sort((a, b) => { const x = key(a), y = key(b); return (y[0] - x[0]) || (y[1] - x[1]); });
  }, [d, f, progs]);

  const warmCells = rows.reduce((s, r) => s + r.warm, 0);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  useEffect(() => { setPage(0); }, [f, period]);
  const pageNo = Math.min(page, pages - 1);
  const shownRows = rows.slice(pageNo * PAGE, pageNo * PAGE + PAGE);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggle = (k, v) => setF(x => ({ ...x, [k]: x[k].includes(v) ? x[k].filter(y => y !== v) : [...x[k], v] }));

  function clickCell(c, p) {
    const open = c.projects.find(x => x.programme === p.slug);
    if (open) { navigate(`/customers/${open.id}`); return; }
    setOpenError(null);
    setCell({ client: c, programme: p });
  }
  async function openProject() {
    setBusy(true);
    try {
      const r = await api.explore.openFromFindClients({ companyId: cell.client.id, programme: cell.programme.slug });
      setCell(null);
      navigate(`/customers/${r.id}`);
    } catch (e) { setOpenError(e.message); }
    finally { setBusy(false); }
  }

  function exportCsv() {
    const head = ['MB', 'Client', 'City', 'Municipality', 'Sector', 'Employees', 'Revenue (RSD m)', 'List', 'Rank in city', 'Potential', ...progs.map(p => p.name)];
    const esc = (v) => { const s = v == null ? '' : String(v); return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [head, ...rows.map(({ c }) => [c.mb, c.name, cityLabel(c.city), c.municipality, c.sector, c.employees, c.revenue != null ? Math.round(c.revenue / 1000) : '', c.list, c.cityRank, c.potential, ...progs.map(p => c.fit[p.slug] || '')])]
      .map(r => r.map(esc).join(';'));
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `HB Find clients - ${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  if (state.loading && !d) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>;
  if (state.error) return <Alert type="error">{state.error}</Alert>;

  return (
    <>
      {/* Criteria and period bar */}
      <Card style={{ padding: '14px 20px', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: '0.9rem', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <b>Criteria and period:</b>
            {d.profile === 'personal'
              ? <span style={{ color: '#8a4b00' }}>personal profile (differs from the team)</span>
              : <span>team profile</span>}
            <span>·</span>
            <span>Period</span>
            <Select value={period} onChange={e => setPeriod(Number(e.target.value))} style={{ width: 'auto', padding: '4px 8px' }}>
              {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
            <span>· market signals in period: {d.signalsInPeriod}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {d.profile === 'personal' && (
              <Btn size="sm" variant="outline" onClick={async () => { await api.explore.resetFitProfile(); load(); }}>Restore team profile</Btn>
            )}
            <Btn size="sm" variant="outline" loading={state.loading} onClick={load}>Refresh data</Btn>
            <Btn size="sm" onClick={() => setAdjust(true)}>Adjust criteria</Btn>
          </div>
        </div>
        {d.territory && <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 4 }}>Territory: {d.territory}</div>}
      </Card>

      {/* Filters */}
      <Card style={{ padding: '16px 20px', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <SectionLabel style={{ marginBottom: 0, flex: 1 }}>Filters</SectionLabel>
          <Btn size="sm" variant="link" onClick={() => setShowFilters(s => !s)}>{showFilters ? 'Hide filters' : 'Show filters'}</Btn>
          <Btn size="sm" variant="link" onClick={() => setF(F0)} style={{ color: 'var(--muted)' }}>Reset</Btn>
        </div>
        {!showFilters && (
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 8 }}>
            {[
              f.city.length ? f.city.map(cityLabel).join(', ') : 'all cities',
              f.list.length ? `list ${f.list.join(' + ')}` : 'all lists',
              f.sector || null, f.municipality || null,
              f.empMin !== '' || f.empMax !== '' ? `${f.empMin || 0}–${f.empMax || '∞'} employees` : null,
              f.revMin !== '' ? `revenue from ${f.revMin} RSD m` : null,
              f.own !== 'any' ? (f.own === 'foreign' ? 'foreign owner' : 'domestic') : null,
              f.noPublic ? 'no public enterprises' : null,
              f.crit.length ? `criteria: ${f.crit.join(', ')}` : null,
              f.progs ? `${progs.length} of ${scored.length} programmes` : null,
              `threshold ${f.min}`,
            ].filter(Boolean).join(' · ')}
          </div>
        )}
        {showFilters && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '14px 22px', marginTop: 14 }}>
            <FilterGroup label="City">
              {options.cities.map(([city, n]) => (
                <Check key={city} on={f.city.includes(city)} onChange={() => toggle('city', city)}>{cityLabel(city)} <small>{n}</small></Check>
              ))}
            </FilterGroup>
            <FilterGroup label="Potential list">
              {[['A', 'A — highest potential'], ['B', 'B — high potential'], ['C', 'C — other']].map(([k, l]) => (
                <Check key={k} on={f.list.includes(k)} onChange={() => toggle('list', k)}>{l}</Check>
              ))}
            </FilterGroup>
            <FilterGroup label="Sector · municipality">
              <Select value={f.sector} onChange={e => set('sector', e.target.value)}>
                <option value="">All sectors</option>
                {options.sectors.map(s => <option key={s}>{s}</option>)}
              </Select>
              <Select value={f.municipality} onChange={e => set('municipality', e.target.value)} style={{ marginTop: 6 }}>
                <option value="">All municipalities</option>
                {options.municipalities.map(s => <option key={s}>{s}</option>)}
              </Select>
            </FilterGroup>
            <FilterGroup label="Employees (from – to) · revenue from (RSD m)">
              <div style={{ display: 'flex', gap: 6 }}>
                <Input type="number" min="0" value={f.empMin} onChange={e => set('empMin', e.target.value)} placeholder="0" />
                <Input type="number" min="0" value={f.empMax} onChange={e => set('empMax', e.target.value)} placeholder="∞" />
              </div>
              <Input type="number" min="0" value={f.revMin} onChange={e => set('revMin', e.target.value)} placeholder="0" style={{ marginTop: 6 }} />
            </FilterGroup>
            <FilterGroup label="Ownership">
              <Select value={f.own} onChange={e => set('own', e.target.value)}>
                <option value="any">All</option>
                <option value="foreign">Foreign owner (likely)</option>
                <option value="domestic">Domestic</option>
              </Select>
              <Check on={f.noPublic} onChange={() => set('noPublic', !f.noPublic)}>Exclude public enterprises</Check>
            </FilterGroup>
            <FilterGroup label="Criteria (any of)">
              {d.criteria.filter(c => c.kind !== 'informative' && c.kind !== 'size').map(c => (
                <Check key={c.name} on={f.crit.includes(c.name)} onChange={() => toggle('crit', c.name)} title={c.description}>{c.name}</Check>
              ))}
            </FilterGroup>
            <FilterGroup label="Programmes">
              {scored.map(p => {
                const on = !f.progs || f.progs.includes(p.slug);
                return <Check key={p.slug} on={on} onChange={() => set('progs', on ? (f.progs || scored.map(x => x.slug)).filter(x => x !== p.slug) : [...(f.progs || []), p.slug])}>{p.name}</Check>;
              })}
            </FilterGroup>
            <FilterGroup label="Show">
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.86rem' }}>
                Threshold <Input type="number" min="0" max="100" step="5" value={f.min} onChange={e => set('min', Number(e.target.value) || 0)} style={{ width: 80 }} />
              </div>
              <Check on={f.onlyWarm} onChange={() => set('onlyWarm', !f.onlyWarm)}>Only firms with a warm cell</Check>
            </FilterGroup>
          </div>
        )}
      </Card>

      {/* Heat map */}
      <Card style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '10px 18px', flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--body-ink)' }}>
            <Kpi n={rows.length} label="firms" />
            <Kpi n={progs.length} label="products" />
            <Kpi n={warmCells} label={`warm cells (≥ ${f.min})`} />
          </div>
          <Btn size="sm" onClick={exportCsv} disabled={!rows.length}>Export to Excel</Btn>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '14px 0 4px' }}>
          <Input value={f.q} onChange={e => set('q', e.target.value)} placeholder="Search by name or MB…" style={{ flex: 1, minWidth: 220 }} />
          <Select value={f.sort} onChange={e => set('sort', e.target.value)} style={{ width: 'auto' }}>
            <option value="potential">Sort: potential</option>
            <option value="max">Sort: strongest fit</option>
            <option value="warm">Sort: warm cells</option>
            <option value="revenue">Sort: revenue</option>
            <option value="employees">Sort: employees</option>
          </Select>
          <Pager page={pageNo} pages={pages} total={rows.length} onPage={setPage} />
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: '8px 0 12px' }}>
          Click a cell to open a project for that client and programme at stage 0 (Selected — before the first conversation). Outlined cells already have your project; click to open it.
        </p>
        {!rows.length ? (
          <p style={{ fontSize: '0.9rem', color: 'var(--muted)' }}>No firm matches these filters.</p>
        ) : (
          <div style={{ overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={{ ...th, ...stickyName, zIndex: 4, textAlign: 'left' }}>Client</th>
                  <th style={th}>List</th>
                  {progs.map(p => (
                    <th key={p.slug} title={p.name} style={{ ...th, writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 150, whiteSpace: 'nowrap', minWidth: 38 }}>{p.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shownRows.map(({ c }) => (
                  <tr key={c.id}>
                    <td style={{ ...td, ...stickyName }}>
                      <div style={{ fontWeight: 700 }} title={c.name}>{(c.short || c.name).length > 48 ? `${(c.short || c.name).slice(0, 46)}…` : (c.short || c.name)}</div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--muted)' }}>{c.sector} · {empLabel(c)} · {cityLabel(c.city)}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'center', fontSize: '0.75rem' }}>{c.list}</td>
                    {progs.map(p => {
                      const v = c.fit[p.slug];
                      const opened = c.projects.some(x => x.programme === p.slug);
                      const warm = v && v >= f.min;
                      const reasons = (c.why[p.slug] || []).join(', ');
                      return (
                        <td key={p.slug}
                          onClick={() => clickCell(c, p)}
                          title={`${p.name}${v ? `: ${v}` : ''}${reasons ? ` — ${reasons}` : ''}${opened ? ' · your project is open' : ''}`}
                          style={{
                            ...td, textAlign: 'center', cursor: 'pointer', minWidth: 38,
                            fontWeight: warm ? 700 : 400, color: warm ? 'var(--ink)' : 'var(--muted)',
                            background: warm ? ramp(v / 100) : 'transparent',
                            outline: opened ? '3px solid var(--ink)' : 'none', outlineOffset: -3,
                          }}>
                          {v || ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
            <Pager page={pageNo} pages={pages} total={rows.length} onPage={(n) => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }} numbers />
          </div>
        )}
        <p style={{ fontSize: '0.76rem', color: 'var(--muted)', margin: '12px 0 0', lineHeight: 1.5 }}>{FIT_METHOD}</p>
      </Card>

      <MarketSignals onChanged={load} />

      {adjust && <AdjustCriteria onClose={() => setAdjust(false)} onSaved={() => { setAdjust(false); load(); }} />}

      <ConfirmDialog
        open={!!cell}
        title="Open a project"
        confirmLabel="Open the project"
        busy={busy}
        error={openError}
        onCancel={() => setCell(null)}
        onConfirm={openProject}
      >
        {cell && <>
          <b>{cell.client.short || cell.client.name}</b> — {cell.programme.name}
          {cell.client.fit[cell.programme.slug] ? ` (fit ${cell.client.fit[cell.programme.slug]})` : ''}.
          <br />The project starts at stage 0, Selected — before the first conversation, with the standard programme.
        </>}
      </ConfirmDialog>
    </>
  );
}

// Criteria weights and programme priorities: try them as a personal profile, or (BP) save them for the team.
function AdjustCriteria({ onClose, onSaved }) {
  const [p, setP] = useState(null);
  const [draft, setDraft] = useState({ weights: {}, priorities: {} });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.explore.getFitProfile().then(d => {
      setP(d);
      const src = d.personal || d.team;
      setDraft({ weights: { ...src.weights }, priorities: { ...src.priorities } });
    }).catch(e => setError(e.message));
  }, []);
  async function save(scope) {
    setBusy(true); setError(null);
    try { await api.explore.saveFitProfile({ scope, ...draft }); onSaved(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  async function restore() {
    setBusy(true);
    try { await api.explore.resetFitProfile(); onSaved(); }
    catch (e) { setError(e.message); setBusy(false); }
  }
  const setW = (k, v) => setDraft(x => ({ ...x, weights: { ...x.weights, [k]: v } }));
  const setPr = (k, v) => setDraft(x => ({ ...x, priorities: { ...x.priorities, [k]: v } }));

  return (
    <Modal open onClose={onClose} title="Adjust criteria" width={640}
      footer={p && <>
        {p.personal && <Btn size="sm" variant="ghost" onClick={restore} disabled={busy}>Restore team profile</Btn>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {p.canSaveTeam && <Btn size="sm" variant="outline" loading={busy} onClick={() => save('team')}>Save as team profile</Btn>}
          <Btn size="sm" loading={busy} onClick={() => save('personal')}>Apply</Btn>
        </div>
      </>}>
      {error && <div style={{ marginBottom: 12 }}><Alert type="error">{error}</Alert></div>}
      {!p ? <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner /></div> : <>
        <p style={{ fontSize: '0.85rem', color: 'var(--body-ink)', marginTop: 0 }}>
          {p.canSaveTeam
            ? `Apply tries these settings for you only; Save as team profile sets them for every Ice Pilot in ${p.territory || 'the network'}.`
            : 'Apply tries these settings for you only — your personal profile. The team profile is set by your Business Partner.'}
        </p>
        <SectionLabel style={{ marginTop: 14 }}>Criteria weights</SectionLabel>
        {p.criteria.map(c => (
          <Row key={c.name} label={c.name} hint={c.description}>
            <Select value={draft.weights[c.name] ?? 1} onChange={e => setW(c.name, Number(e.target.value))} style={{ width: 140 }}>
              {WEIGHT_LEVELS.map(([l, v]) => <option key={l} value={v}>{l}</option>)}
            </Select>
          </Row>
        ))}
        <SectionLabel style={{ marginTop: 18 }}>Programme priorities</SectionLabel>
        {p.programmes.map(pr => (
          <Row key={pr.slug} label={pr.name}>
            <Select value={draft.priorities[pr.slug] ?? 0} onChange={e => setPr(pr.slug, Number(e.target.value))} style={{ width: 190 }}>
              {PRIORITY_LEVELS.map(([l, v]) => <option key={l} value={v}>{l}</option>)}
            </Select>
          </Row>
        ))}
      </>}
    </Modal>
  );
}

// 1–50 of 1,078  ‹ ›  (with page numbers under the list)
function Pager({ page, pages, total, onPage, numbers }) {
  if (!total) return null;
  const from = page * PAGE + 1, to = Math.min(total, (page + 1) * PAGE);
  const nums = [];
  for (let i = 0; i < pages; i++) if (i === 0 || i === pages - 1 || Math.abs(i - page) <= 2) nums.push(i); else if (nums[nums.length - 1] !== '…') nums.push('…');
  const btn = (on) => ({ minWidth: 32, padding: '4px 8px', border: '1px solid var(--line)', borderRadius: 8, background: on ? 'var(--ink)' : 'var(--canvas-white)', color: on ? '#fff' : 'var(--ink)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' });
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.82rem', color: 'var(--body-ink)', flexWrap: 'wrap' }}>
      <span style={{ marginRight: 4 }}>{from}–{to} of {total.toLocaleString('en-GB')}</span>
      <button style={btn(false)} disabled={page === 0} onClick={() => onPage(page - 1)} aria-label="Previous page">‹</button>
      {numbers && nums.map((n, i) => n === '…'
        ? <span key={`e${i}`}>…</span>
        : <button key={n} style={btn(n === page)} onClick={() => onPage(n)} aria-current={n === page ? 'page' : undefined}>{n + 1}</button>)}
      <button style={btn(false)} disabled={page >= pages - 1} onClick={() => onPage(page + 1)} aria-label="Next page">›</button>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '7px 0', borderBottom: '1px solid var(--line)' }}>
      <div style={{ flex: 1, minWidth: 0, fontSize: '0.88rem' }} title={hint}>{label}</div>
      {children}
    </div>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div>
      <SectionLabel style={{ marginBottom: 6 }}>{label}</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>{children}</div>
    </div>
  );
}

function Check({ on, onChange, children, title }) {
  return (
    <label title={title} style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: '0.86rem', cursor: 'pointer', lineHeight: 1.35 }}>
      <input type="checkbox" checked={on} onChange={onChange} style={{ marginTop: 3 }} />
      <span>{children}</span>
    </label>
  );
}

function Kpi({ n, label }) {
  return (
    <div>
      <b style={{ display: 'block', fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--ink)' }}>{n.toLocaleString('en-GB')}</b>
      {label}
    </div>
  );
}

const th = { position: 'sticky', top: 0, background: 'var(--canvas-white)', zIndex: 3, fontSize: '0.72rem', fontWeight: 700, color: 'var(--body-ink)', borderBottom: '2px solid var(--ink-mid)', verticalAlign: 'bottom', padding: '6px 4px' };
const td = { padding: '5px 6px', borderBottom: '1px solid var(--line)', verticalAlign: 'top' };
const stickyName = { position: 'sticky', left: 0, background: 'var(--canvas-white)', zIndex: 1, minWidth: 220, maxWidth: 300 };
